import type { ReactNode } from 'react';
import { ButtonGlyph } from '../common/PendingButton';
import { DisclosureRegion, Icon } from '../common';
import { lapsed } from './requestFacts';
import type { BookingDetail, Owner } from './types';

// THE REQUEST CARD — the first card in the Self-service column (Freddi,
// 2026-10-10; docs/design/accept-or-decline.html, docs/plan/accept-or-
// decline-request-card.md). The decision moved out of the middle section,
// which now carries nothing about the request but the lock on its fields.
//
// G24's four grounds, used exactly: the tone answers "what does this want
// from me?" before a word is read. Blue waits on Patricia, gold on the
// customer, green is live and fine, grey is finished. The card says what
// happened to the REQUEST; the bar's status says what the BOOKING now is —
// which is why Accepted and Unconfirmed appear together and do not disagree.
//
// What the design CUT, each because something else on screen says it: the
// capacity line (the Availability fold), the holding line (the gold ground),
// the dates (the Stay card), the faces (the bar), provenance (the bar's chip),
// "she is not expecting a reply" and the outcome sentences. No undo in v1
// (Freddi, 2026-10-10). Do not put them back without asking him.
//
// THE CARD CARRIES THE COLUMN'S HEADING (Freddi, 2026-10-10: "merge the self
// service title and info text into this blue container … and add the
// requested pill to the top right"). On an app booking the column's own head
// goes; *Self-service* and its line lead the card, the state pill at the top
// right. It still opens and closes ("we should still be able to open/close"):
// the head row is the toggle, the chevron after the pill; shut, only the head
// shows.

// `desk` is the fifth, grey state (Freddi, 2026-10-10: "a grey state when we
// submit a booking rather than the customer"): a booking the desk made, so
// the column is the same card on every booking.
export type CardState = 'requested' | 'accepted' | 'confirmed' | 'declined' | 'desk';

export function cardState(b: Pick<BookingDetail, 'status' | 'source'>): CardState | null {
  if (b.status === 'requested') return 'requested';
  if (b.status === 'declined') return 'declined';
  if (b.source !== 'portal') return 'desk';
  if (b.status === 'unconfirmed') return 'accepted';
  if (b.status === 'confirmed' || b.status === 'arrived' || b.status === 'collected') return 'confirmed';
  return null;
}

const euro = (n: number) => (Number.isInteger(n) ? `€${n}` : `€${n.toFixed(2)}`);
// CONFIRMED READS BOOKED (Freddi, 2026-10-10): Unconfirmed to Confirmed means
// the deposit is in — the stay is booked.
const WORD: Record<CardState, string> = { requested: 'Requested', accepted: 'Accepted', confirmed: 'Booked', declined: 'Declined', desk: 'Desk booking' };
// The desk's glyph is the pencil — typed here — so grey never reads as Declined.
const GLYPH: Record<CardState, string> = { requested: 'clipboard-check', accepted: 'pin', confirmed: 'calendar-check', declined: 'circle-x', desk: 'edit' };

// THE GLANCE LINE IS GONE (Freddi, 2026-10-11: "Deposit paid · €80 due at
// collection — this part should be removed"): the Money card says the money,
// and the line under the title says what happened. One truth per card.

/** The line under *Self-service*: what has happened, in this state. */
export function subLine(state: CardState, first: string): string {
  switch (state) {
    case 'requested': return `${first} has submitted a booking request`;
    case 'accepted': return `You accepted ${first}’s request`;
    case 'confirmed': return `${first}’s deposit is in and the stay is booked`;
    case 'declined': return `You declined ${first}’s request`;
    case 'desk': return 'Booked at the desk';
  }
}

export interface RequestCardProps {
  booking: BookingDetail;
  owner: Owner;
  today: string;
  onAccept: () => void;
  onDecline: () => void;
  busy: boolean;
  /** Which decision is in flight, so the button can say so (2026-10-11). */
  busyVerdict?: 'accept' | 'decline' | null;
  /** App access, held inside the container (Freddi, 2026-10-10). */
  children?: ReactNode;
  /** The Status control's pick, before Save — the card follows it at once (Freddi, 2026-10-10). */
  status?: BookingDetail['status'];
  /** Decided, and the booking re-reading: the buttons show as a skeleton till it lands (Freddi, 2026-10-14). */
  settling?: boolean;
}

/** The card with its fold held by the caller — what the tests draw open or shut. */
export function RequestCard(props: RequestCardProps) {
  // THE HEAD IS A LABEL, NOT A CONTROL (Freddi, 2026-10-11: "the arrow should
  // be removed from the top right of the self service card and the booked
  // status pill should appear in the top right corner and that part of the
  // card should not be expandable"). The card's own content — the glance, her
  // note, Accept and Decline — is always on show; what opens is each person
  // in the household, to the number, the message and the link (AccessColumn's
  // rows, which ride in here through the frame).
  return <RequestCardView {...props} />;
}

export function RequestCardView({ booking: saved, owner, today, onAccept, onDecline, busy, busyVerdict = null, children, status, settling = false }: RequestCardProps) {
  // The card reads the status as the bar now has it, saved or not.
  const booking = status && status !== saved.status ? { ...saved, status } : saved;
  const state = cardState(booking);
  // No card for this state (an unsaved pick of Cancelled) — but App access is
  // the household's, not the card's, so the fold stays (PR #251 review).
  if (!state) return children ? <>{children}</> : null;
  // WHO SENT IT: the contact who wrote the note when there is one; the first
  // on the household otherwise (PR #251 review). A request with no note has
  // no author on the booking row — the activity record has it, and reading
  // that here is a second round trip for a first name; the common case, one
  // registered contact, is right either way.
  const author = booking.notesBy != null ? owner.contacts.find((c) => c.id === booking.notesBy) : undefined;
  const contact = author ?? owner.contacts[0];
  const first = (contact as { firstName?: string | null } | undefined)?.firstName || contact?.name.split(' ')[0] || 'the customer';
  // Her words only on her booking: a desk booking carries no customer's note.
  const note = state !== 'desk' && booking.notes && booking.notesBy != null ? booking.notes : null;
  const gone = lapsed(booking, today);
  return (
    <div className={`rq is-${state}`} aria-live="polite">
      <div className="rq-top">
        {/* ONE ROW, ONE CENTRE LINE: the title and the pill, the pill last at
            the top right; the line under the title takes the card's full width. */}
        <span className="rq-title">Self-service</span>
        <span className="rq-stat"><Icon name={GLYPH[state]} size={13} />{WORD[state]}</span>
        {/* THE LINE SAYS WHAT HAPPENED, PER STATE (Freddi, 2026-10-10). */}
        <span className="rq-sub">{subLine(state, first)}</span>
      </div>
      {note && (
        <div className="rq-body">
          <div className="rq-note">
            <div className="sh"><Icon name="message" size={11} />What {first} wrote</div>
            <q>{note}</q>
          </div>
        </div>
      )}
      {/* THE DECISION SLIDES AWAY (Freddi, 2026-10-11: "seamless, natural"): the buttons
          live in the shared disclosure, open while the request waits and sliding shut
          the moment it is decided, so the card eases to its new height rather than
          dropping 44px in a frame. While in flight the pressed button says so. */}
      {/* ACCEPTING, THEN A SKELETON, THEN THE CARD AS IT NOW IS (Freddi, 2026-10-14): the pressed button
          spins while the server works; while the booking re-reads the buttons are skeleton bars in the
          same place; then the card's new state, the region sliding shut as it always has. */}
      <DisclosureRegion open={state === 'requested'}>
        {settling && state === 'requested' ? (
          <div className="rq-btns is-skeleton" aria-busy="true">
            <span className="bl-block" style={{ display: 'inline-block', flex: 1, height: 34, borderRadius: 'var(--radius-ctl)' }} />
            <span className="bl-block" style={{ display: 'inline-block', flex: 1, height: 34, borderRadius: 'var(--radius-ctl)' }} />
          </div>
        ) : (
          <div className="rq-btns">
            {!gone && (
              <button type="button" className={'rq-yes' + (busyVerdict === 'accept' ? ' is-busy' : '')} onClick={onAccept} disabled={busy}>
                <ButtonGlyph icon={<Icon name="check" size={13} />} busy={busyVerdict === 'accept'} />{busyVerdict === 'accept' ? 'Accepting…' : 'Accept'}
              </button>
            )}
            <button type="button" className={'rq-no' + (busyVerdict === 'decline' ? ' is-busy' : '')} onClick={onDecline} disabled={busy}>
              <ButtonGlyph icon={<Icon name="circle-x" size={13} />} busy={busyVerdict === 'decline'} />{busyVerdict === 'decline' ? 'Declining…' : 'Decline'}
            </button>
          </div>
        )}
      </DisclosureRegion>
      {/* THE HOUSEHOLD LIVES IN HERE (Freddi, 2026-10-10; 2026-10-11), below a
          hairline: one row per person, each opening to what App access knows
          about them — the rows come from the column through the frame. */}
      {children && <div className="rq-access">{children}</div>}
    </div>
  );
}

/** The household's rows while they load: one row in their shape, so the card is as tall before as after. */
export function PeopleSkeleton() {
  return (
      // The loaded row's own shape (2026-10-12): the avatar, the name's line, the state's line, the chevron.
      <div className="ac-row is-skeleton" aria-busy="true">
        <span className="bl-block" style={{ width: 32, height: 32, borderRadius: '50%' }} />
        <span><span className="bl-block" style={{ display: 'block', width: '64%', height: 11 }} /><span className="bl-block" style={{ display: 'block', width: '78%', height: 9, marginTop: 6 }} /></span>
        <span className="bl-block" style={{ width: 18, height: 22, borderRadius: 'var(--radius-sm)' }} />
      </div>
  );
}

/**
 * THE SAME FRAME WITH NO STAY OPEN (Freddi, 2026-10-11: "the self service
 * column … should appear the same even when the household modal is open").
 * Self-service and its line, no pill — there is no booking to have a state —
 * and the household's people inside, exactly where a stay's card holds them.
 */
export function HouseholdCard({ children }: { children?: ReactNode }) {
  return (
    <div className="rq is-household">
      <div className="rq-top"><span className="rq-title">Self-service</span><span className="rq-sub">Who can sign in for this household</span></div>
      {children && <div className="rq-access">{children}</div>}
    </div>
  );
}
